import { useId, useRef, useState } from 'react';
import { CLIENT_REFUSAL, checkPatient, cleanName, type Patient } from '@/domain';
import { useAddPatient, useRenamePatient } from '@/application/usecases/useClients';
import { strings } from '@/shared/strings';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { modalStyles } from '@/ui/primitives/Modal';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { toast } from '@/ui/primitives/toastStore';
import { useConfirmAction } from '../shell/useConfirmAction';
import { formRefusal, statusOf } from './clientRefusal';
import styles from './ClientForm.module.css';

/** The one thing a patient's form holds, by the name the server refuses it under. */
const FIELDS = ['name'] as const;

/** Whether the server refused the name because another patient already has it. */
function nameIsTaken(error: unknown): boolean {
  return (
    statusOf(error) === 409 &&
    error instanceof Error &&
    error.message === CLIENT_REFUSAL.patientExists
  );
}

export interface PatientFormSheetProps {
  /** The record being renamed. Left off to add a patient. */
  patient?: Patient;
  onClose: () => void;
  /** The record as the server answered it: the new patient, or the renamed one. */
  onDone: (patient: Patient) => void;
  /** The copy on screen is out of date: somebody changed or deleted the record meanwhile. */
  onStale?: () => void;
}

/**
 * A patient's record, added or renamed, in a sheet. The request of
 * 6 October 2026 (second), item 4, as the request of 6 October 2026 (third),
 * item 3, changed it.
 *
 * A patient is a name and nothing else: one record whichever doctors they are
 * seen by, tied to a doctor by a case and not by this form. So both adding and
 * renaming ask for the name alone.
 *
 * Checked before it is asked about, in the server's words. What the server
 * alone can say comes back above the buttons: a copy somebody changed since,
 * and, more often, that another patient already has the name. That one is not
 * a wall. Two people do share a name, so the sheet then offers the second
 * record by name, `Add another patient with this name`, and sends the same
 * name again saying it is meant. Never by accident: it is a second press, with
 * a question of its own for the roles that are asked, and it goes as soon as
 * the name is typed over.
 */
export function PatientFormSheet({ patient, onClose, onDone, onStale }: PatientFormSheetProps) {
  const add = useAddPatient();
  const rename = useRenamePatient();
  const ask = useConfirmAction();
  const nameId = useId();
  const nameInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(patient?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // The name the server said somebody has, exactly as it was sent: the offer is for that name only.
  const [taken, setTaken] = useState<string | null>(null);

  const renaming = patient !== undefined;
  const pending = add.isPending || rename.isPending;
  const dirty = name !== (patient?.name ?? '');

  function typed(value: string) {
    setName(value);
    // Whatever was said about the last name is not said about this one.
    setError(null);
    setMessage(null);
    setTaken(null);
  }

  function refused(failure: unknown, sent: string): void {
    const sameName = nameIsTaken(failure);
    // Changed or deleted by somebody else meanwhile: what is on screen is no longer the record.
    if (renaming && !sameName && (statusOf(failure) === 409 || statusOf(failure) === 404)) onStale?.();

    const refusal = formRefusal(failure, FIELDS);
    if (refusal === null) throw failure;
    setError(refusal.fields.name ?? null);
    setMessage(refusal.message);
    setTaken(sameName ? sent : null);
  }

  /** `second` is the press that says the same name is meant: a different person, a second record. */
  function submit(second: boolean) {
    const problem = checkPatient({ name });
    setError(problem?.message ?? null);
    // The second press is asked about first: until it is answered, what the server said still stands,
    // so Cancel comes back to the sentence and the offer under it.
    if (!second) {
      setMessage(null);
      setTaken(null);
    }
    if (problem !== null) {
      nameInput.current?.focus();
      return;
    }

    const cleaned = cleanName(name);
    const sameName = second ? { allowSameName: true } : {};

    if (renaming) {
      if (cleaned === patient.name) {
        onClose();
        return;
      }
      ask(
        second
          ? strings.clients.confirmRenameSameName(patient.name, cleaned)
          : strings.clients.confirmRenamePatient(patient.name, cleaned),
        () =>
          rename
            .mutateAsync({ id: patient.id, rowVersion: patient.rowVersion, name: cleaned, ...sameName })
            .then(
              (saved) => {
                toast(strings.clients.patientRenamed(saved.name), { tone: 'success' });
                onDone(saved);
              },
              (failure: unknown) => refused(failure, cleaned),
            ),
        {
          title: strings.clients.renamePatientTitle(patient.name),
          confirmLabel: second ? strings.clients.renameSameName : strings.clients.rename,
        },
      );
      return;
    }

    ask(
      second
        ? strings.clients.confirmAddSameName(cleaned)
        : strings.clients.confirmAddPatient(cleaned),
      () =>
        add.mutateAsync({ name: cleaned, ...sameName }).then(
          (made) => {
            toast(strings.clients.patientAdded(made.name), { tone: 'success' });
            onDone(made);
          },
          (failure: unknown) => refused(failure, cleaned),
        ),
      {
        title: strings.clients.addPatientTitle,
        confirmLabel: second ? strings.clients.addSameName : strings.clients.addPatient,
      },
    );
  }

  return (
    <BottomSheet
      title={
        renaming
          ? strings.clients.renamePatientTitle(patient.name)
          : strings.clients.addPatientTitle
      }
      lead={renaming ? strings.clients.renamePatientLead : strings.clients.addPatientLead}
      closeLabel={strings.sheet.close}
      onClose={onClose}
      unsaved={
        dirty
          ? {
              question: strings.clients.discardQuestion,
              keep: strings.sheet.keep,
              discard: strings.sheet.discard,
            }
          : null
      }
      footer={
        <>
          {message !== null && (
            <p className={styles.message} role="alert">
              {message}
            </p>
          )}
          {/* Under the sentence that says the name exists, and only for the name it was said of. */}
          {taken !== null && (
            <Button className={styles.offer} block disabled={pending} onClick={() => submit(true)}>
              {renaming ? strings.clients.renameSameName : strings.clients.addSameName}
            </Button>
          )}
          <div className={sheetStyles.actions}>
            <Button onClick={onClose} disabled={pending}>
              {strings.clients.cancel}
            </Button>
            <Button
              variant="primary"
              className={sheetStyles.primary}
              loading={pending}
              onClick={() => submit(false)}
            >
              {strings.clients.save}
            </Button>
          </div>
        </>
      }
    >
      <div className={styles.form}>
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={nameId}>
            {strings.clients.patientName}
          </label>
          <input
            ref={nameInput}
            id={nameId}
            className={modalStyles.input}
            value={name}
            autoComplete="off"
            autoCapitalize="words"
            placeholder={strings.clients.patientNamePlaceholder}
            aria-required="true"
            aria-invalid={error !== null || undefined}
            aria-describedby={error === null ? undefined : `${nameId}-error`}
            data-autofocus
            onChange={(event) => typed(event.target.value)}
          />
          {error !== null && (
            <p id={`${nameId}-error`} className={modalStyles.error}>
              {error}
            </p>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
